import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline'
export type ButtonSize = 'xs' | 'sm' | 'md'

const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-accent-ink shadow-[0_1px_2px_rgb(0_0_0/0.12)] hover:bg-accent-hover disabled:hover:bg-accent',
  secondary: 'bg-subtle text-ink hover:bg-line/70 disabled:hover:bg-subtle',
  outline: 'bg-surface text-ink shadow-card hover:bg-subtle disabled:hover:bg-surface',
  ghost: 'text-ink-muted hover:bg-subtle hover:text-ink disabled:hover:bg-transparent',
  danger: 'bg-danger text-white hover:bg-danger/90 dark:text-canvas',
}

const sizes: Record<ButtonSize, string> = {
  xs: 'h-7 gap-1.5 rounded-full px-2.5 text-xs',
  sm: 'h-8 gap-1.5 rounded-full px-3.5 text-[13px]',
  md: 'h-10 gap-2 rounded-full px-5 text-sm',
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
}

function buttonClasses(variant: ButtonVariant = 'primary', size: ButtonSize = 'md') {
  return cn(
    'pressable inline-flex shrink-0 select-none items-center justify-center whitespace-nowrap font-medium',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
    'disabled:cursor-not-allowed disabled:opacity-50',
    variants[variant],
    sizes[size],
  )
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', className, type = 'button', ...props },
  ref,
) {
  return <button ref={ref} type={type} className={cn(buttonClasses(variant, size), className)} {...props} />
})
